<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { NCard, NInput, NButton, NAlert, NSpace, NText, NForm, NFormItem } from 'naive-ui'
import { login, isLoading, authError } from '@/services/auth'
import { getCurrentLanguage } from '@/services/language'
import OAuthButton from '@/components/OAuthButton.vue'
import { dt } from '@/utils/i18n'

const router = useRouter()
const route = useRoute()

const email = ref('')
const submitStatus = ref(null)
const successMessage = ref('')
const errorMessage = ref('')

// For OAuth
const language = ref(getCurrentLanguage())
const role = ref(route.query.role || 'patient')

// ✅ Add computed for role check
const isProvider = computed(() => role.value === 'provider')

const returnUrl = computed(() => route.query.returnUrl || '/')

const handleLogin = async () => {
  if (!email.value) {
    return
  }
  
  try {
    submitStatus.value = 'submitting'
    const result = await login(email.value, language.value)
    submitStatus.value = 'success'
    successMessage.value = result.message
    
    // Store data for verification
    localStorage.setItem('pending_verification_email', email.value)
    localStorage.setItem('pending_user_id', result.user_id)
    localStorage.setItem('pending_validation_type', result.verification_type)
        
    // Redirect to verification page
    router.push({
      name: 'verify',
      query: { returnUrl: returnUrl.value }
    })
  } catch (error) {
    submitStatus.value = 'error'
    errorMessage.value = error.message || 'Login failed'
  }
}

const handleOAuthSuccess = (userData) => {
  // The redirect to home should happen automatically in the OAuth service
}

const handleOAuthError = (error) => {
  submitStatus.value = 'error'
  errorMessage.value = error.message || 'OAuth login failed'
}

// ✅ Updated to pass role
const goToRegister = () => {
  router.push({
    name: 'register',
    query: { 
      role: role.value,           // ✅ Add role
      returnUrl: returnUrl.value 
    }
  })
}

// ✅ Add role switcher
const switchRole = (newRole) => {
  role.value = newRole
  router.push({ 
    query: { 
      role: newRole,
      returnUrl: returnUrl.value 
    } 
  })
}
</script>

<template>
  <div class="auth-page" :class="{ 'provider-theme': isProvider }">
    <div class="auth-container">
      <NCard class="auth-card">
        <!-- ✅ Role Badge -->
        <template #header>
          <div class="card-header">
            <div class="role-badge" :class="{ 'provider': isProvider }">
              {{ isProvider ? '👨‍⚕️ Healthcare Provider Portal' : '👤 Patient Portal' }}
            </div>
            <h1 class="card-title">{{ dt('login.title') }}</h1>
          </div>
        </template>

        <div class="card-content">
          <!-- ✅ Role Switcher Tabs -->
          <div class="role-tabs">
            <button
              @click="switchRole('patient')"
              class="role-tab"
              :class="{ active: !isProvider }"
            >
              Patient
            </button>
            <button
              @click="switchRole('provider')"
              class="role-tab"
              :class="{ active: isProvider }"
            >
              Provider
            </button>
          </div>

          <div v-if="submitStatus === 'error'" class="mb-4">
            <NAlert type="error" :title="errorMessage || authError" />
          </div>

          <div v-if="submitStatus === 'success'" class="mb-4">
            <NAlert type="success" :title="successMessage" />
          </div>

          <NForm>
            <NFormItem :label="dt('login.emailLabel')" required>
              <NInput
                v-model:value="email"
                type="email"
                :placeholder="dt('login.emailPlaceholder')"
                :disabled="submitStatus === 'submitting'"
                @keydown.enter="handleLogin"
              />
            </NFormItem>

            <div class="form-actions">
              <NButton
                type="primary"
                block
                :loading="submitStatus === 'submitting'"
                :disabled="!email"
                @click="handleLogin"
                class="login-button"
              >
                {{ dt('login.title') }}
              </NButton>
            </div>
          </NForm>

          <!-- OAuth Section -->
          <div class="oauth-section">
            <div class="divider">
              <span>{{ dt('login.socialLoginPrompt') }}</span>
            </div>

            <div class="oauth-buttons">
              <OAuthButton 
                provider="google" 
                :role="role" 
                :language="language"
                @success="handleOAuthSuccess" 
                @error="handleOAuthError" 
              />
              <OAuthButton 
                provider="github" 
                :role="role" 
                :language="language"
                @success="handleOAuthSuccess" 
                @error="handleOAuthError" 
              />
            </div>

            <p class="oauth-note">
              {{ dt('login.privacyNote') }}
            </p>
          </div>

          <div class="auth-options">
            <NText depth="3">{{ dt('login.noAccountPrompt') }}</NText>
            <NButton text @click="goToRegister" class="register-link">
              {{ dt('register.title') }}
            </NButton>
          </div>
        </div>
      </NCard>

      <div class="auth-info" :class="{ 'provider': isProvider }">
        <h2>{{ dt('login.welcomeHeader') }}</h2>
        <p>{{ dt('login.welcomeSubheader') }}</p>
        <ul>
          <li>{{ dt('login.benefits.languageSupport') }}</li>
          <li>{{ dt('login.benefits.loginOptions') }}</li>
          <li>{{ dt('login.benefits.noPasswords') }}</li>
          <li>{{ dt('login.benefits.secureAccess') }}</li>
          <li>{{ dt('login.benefits.favorites') }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ✅ CSS Variables for Theming */
.auth-page {
  --primary-color: #3498db;
  --primary-hover: #2980b9;
  --gradient-start: #667eea;
  --gradient-end: #764ba2;
  --badge-bg: #e3f2fd;
  --badge-color: #1976d2;
}

.auth-page.provider-theme {
  --primary-color: #16a085;
  --primary-hover: #138f7a;
  --gradient-start: #16a085;
  --gradient-end: #1abc9c;
  --badge-bg: #e0f2f1;
  --badge-color: #00796b;
}

.auth-page {
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f8f9fa;
}

.auth-container {
  display: flex;
  max-width: 900px;
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
}

.auth-card {
  flex: 1;
  border-radius: 0;
  box-shadow: none;
}

/* ✅ Card Header Styling */
.card-header {
  padding-bottom: 1rem;
}

.role-badge {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 1rem;
  background: var(--badge-bg);
  color: var(--badge-color);
  transition: all 0.3s ease;
}

.card-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
}

/* ✅ Role Tabs */
.role-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 1.5rem;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 4px;
}

.role-tab {
  flex: 1;
  padding: 10px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
  color: #666;
}

.role-tab.active {
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  color: var(--primary-color);
  font-weight: 600;
}

.role-tab:hover:not(.active) {
  background: rgba(255, 255, 255, 0.5);
}

.card-content {
  padding: 1rem 0;
}

.form-actions {
  margin-top: 2rem;
}

/* ✅ Login Button with Theme Colors */
.login-button {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  transition: all 0.3s ease;
}

.login-button:hover {
  background-color: var(--primary-hover) !important;
  border-color: var(--primary-hover) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* ✅ Register Link with Theme Color */
.register-link {
  color: var(--primary-color) !important;
  font-weight: 600;
}

.register-link:hover {
  color: var(--primary-hover) !important;
  text-decoration: underline;
}

.oauth-section {
  margin-top: 16px;
  width: 100%;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 12px 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #e5e7eb;
}

.divider span {
  padding: 0 12px;
  color: #6b7280;
  font-size: 14px;
}

.oauth-note {
  margin-top: 12px;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
}

.auth-options {
  margin-top: 2rem;
  text-align: center;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
}

/* ✅ Info Panel with Theme Gradient */
.auth-info {
  flex: 1;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  color: white;
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: background 0.3s ease;
}

.auth-info h2 {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.auth-info p {
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  opacity: 0.9;
}

.auth-info ul {
  list-style-type: none;
  padding: 0;
}

.auth-info li {
  margin-bottom: 0.8rem;
  display: flex;
  align-items: center;
  font-size: 1.1rem;
}

.auth-info li::before {
  content: "✓";
  margin-right: 0.5rem;
  font-weight: bold;
}

.mb-4 {
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .auth-container {
    flex-direction: column-reverse;
  }

  .auth-info {
    padding: 2rem;
  }

  .role-tabs {
    margin-bottom: 1rem;
  }
}
</style>